import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

interface CacheEntry {
  data: any;
  timestamp: number;
  expiry: number;
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private cache = new Map<string, CacheEntry>();
  private readonly DEFAULT_EXPIRY = 5 * 60 * 1000; // 5 minuti

  constructor() {
    // Pulisci la cache ogni 10 minuti
    setInterval(() => this.cleanExpiredEntries(), 10 * 60 * 1000);
  }

  /**
   * Memorizza i dati nella cache
   */
  set(key: string, data: any, expiry: number = this.DEFAULT_EXPIRY): void {
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      expiry: expiry
    };
    this.cache.set(key, entry);
  }

  /**
   * Recupera i dati dalla cache
   */
  get(key: string): any {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Controlla se la cache è scaduta
    if (Date.now() - entry.timestamp > entry.expiry) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Verifica se un elemento è presente nella cache e non scaduto
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Rimuove un elemento dalla cache
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Pulisce tutta la cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Wrapper per Observable con caching automatico
   */
  cacheObservable<T>(key: string, observable: Observable<T>, expiry?: number): Observable<T> {
    const cachedData = this.get(key);
    
    if (cachedData) {
      return of(cachedData);
    }

    return observable.pipe(
      tap(data => this.set(key, data, expiry))
    );
  }

  /**
   * Pulisce le entry scadute
   */
  private cleanExpiredEntries(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.expiry) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Ottiene informazioni sulla cache
   */
  getCacheInfo(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
} 